/**
 * RoleDetailPage.jsx — Employer v1.1 Phase 0.6 Slice A.
 *
 * Read-only role detail surface. Shows:
 *   - Title + status badge + key timestamps
 *   - Structured definition summary (must-have domain, comp band,
 *     working model, location, responsibilities, must/nice skills)
 *   - Criteria mirror summary (dimension weights, required/preferred
 *     criteria, dealbreakers)
 *   - Lifecycle Timeline sidebar (Phase 0.3 audit feed)
 *
 * Slice A: NO write affordances. Edit / Publish / Close / Archive
 * land in Slice B/C/D. A single "Edit draft" link surfaces for draft
 * status pointing to the legacy editor (`/edit`) — preserves
 * pre-Phase-0.6 editing capability until Slice B replaces it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, Archive, ArrowLeft, Briefcase, Clock, Copy, Edit3,
  Loader2, RefreshCw, ShieldAlert, Trash2, XCircle,
} from 'lucide-react';

import {
  archiveRole, closeRole, cloneRole, getRole, getRoleCriteria,
} from '@/lib/employer/rolesApi';
import RoleStatusBadge from './RoleStatusBadge';
import CloseRoleModal from './CloseRoleModal';
import ArchiveRoleModal from './ArchiveRoleModal';
import RoleCandidatesWorkspace from './RoleCandidatesWorkspace';
import RoleUploadPanel from './RoleUploadPanel';
import RoleStatusTab from './RoleStatusTab';
import RoleUsageStrip from './RoleUsageStrip';
import RoleBulkEvalJobCard from './RoleBulkEvalJobCard';
import RoleCriteriaSummary from './RoleCriteriaSummary';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

function fmtTs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year:   'numeric',
    month:  'short',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

function isList(x) { return Array.isArray(x) && x.length > 0; }
function isObj(x)  { return x && typeof x === 'object' && !Array.isArray(x); }

export default function RoleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [role,     setRole]     = useState(null);
  const [criteria, setCriteria] = useState(null);
  // Slice β — bump to trigger candidate list refresh when a bulk job terminates.
  const [candidatesRefreshToken, setCandidatesRefreshToken] = useState(0);
  // RC 1.62 Phase B — tabs are now controlled so that the Upload
  // tab can hand off to the Candidates tab on a successful upload.
  // Default lands on "definition" (Phase A operator-locked).
  const [activeTab, setActiveTab] = useState('definition');
  // Feb 2026 workflow correction (D-1 RC-1.b) — the previous
  // implementation auto-switched to the Candidates tab whenever a
  // bulk batch reached terminal success, hiding the WP8 §H.1/§H.2
  // completion summary and the "Review flagged dossiers" CTA. Per
  // Founder directive we now navigate ONLY for single-dossier
  // uploads; bulk-batch completion leaves the operator on the
  // Upload/Bulk Dossiers surface so the authoritative completion
  // summary and next actions are visible. Bulk navigation must
  // occur through explicit user action.
  const onUploadSuccess = useCallback((kind) => {
    setCandidatesRefreshToken((t) => t + 1);
    if (kind === 'single') {
      setActiveTab('candidates');
    }
  }, []);

  // WP8 §H.2 / §H.5 — CTA coordinator. The "Review flagged dossiers"
  // CTA in the bulk-completion summary and the "Review dossiers
  // awaiting decision" CTA in the BulkEvalConfirmDialog both land
  // here. Behaviour: switch to the Candidates tab and push
  // `awaiting_disposition` into the child panel, which activates
  // the corresponding integrity filter chip.
  const [pendingIntegrityFilter, setPendingIntegrityFilter] = useState(null);
  const onReviewFlaggedIntegrity = useCallback(() => {
    setPendingIntegrityFilter('awaiting_disposition');
    setActiveTab('candidates');
  }, []);
  const clearIntegrityFilterRequest = useCallback(() => {
    setPendingIntegrityFilter(null);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await getRole(id);
      if (!r) {
        setError('Role not found');
        setRole(null);
        setCriteria(null);
        return;
      }
      setRole(r);
      try {
        const c = await getRoleCriteria(id);
        setCriteria(c);
      } catch (_e) {
        // Criteria mirror absent for never-published drafts is normal;
        // the GET returns { criteria_exists: false } in that case, so
        // a 4xx here is a genuine error and we surface it inline.
        setCriteria(null);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load role');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ─── Slice D — close + archive dispatch state ──────────────
  const [closeModalOpen,   setCloseModalOpen]   = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [actionPending,    setActionPending]    = useState(false);
  const [actionError,      setActionError]      = useState(null);

  const openCloseModal = useCallback(() => {
    setActionError(null);
    setCloseModalOpen(true);
  }, []);
  const openArchiveModal = useCallback(() => {
    setActionError(null);
    setArchiveModalOpen(true);
  }, []);
  const cancelAction = useCallback(() => {
    if (actionPending) return;
    setCloseModalOpen(false);
    setArchiveModalOpen(false);
    setActionError(null);
  }, [actionPending]);

  const formatBackendError = (e) => {
    const d = e?.detail ?? e?.message ?? 'Action failed';
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.detail || d.message || d.code || 'Action failed';
    return 'Action failed';
  };

  const confirmClose = useCallback(async (hard) => {
    if (!role?.id) return;
    setActionPending(true);
    setActionError(null);
    try {
      const resp = await closeRole(role.id, { immediate: Boolean(hard) });
      if (resp?.already_terminal) {
        toast.message('Role was already closed.');
      } else {
        toast.success(hard ? 'Role closed.' : 'Role is closing.');
      }
      setCloseModalOpen(false);
      await load();
    } catch (e) {
      setActionError(formatBackendError(e));
    } finally {
      setActionPending(false);
    }
  }, [role?.id, load]);

  const confirmArchive = useCallback(async () => {
    if (!role?.id) return;
    setActionPending(true);
    setActionError(null);
    try {
      const resp = await archiveRole(role.id);
      if (resp?.already_terminal) {
        toast.message('Role was already archived.');
      } else if (role.status === 'draft') {
        toast.success('Draft discarded.');
      } else {
        toast.success('Role archived.');
      }
      setArchiveModalOpen(false);
      await load();
    } catch (e) {
      setActionError(formatBackendError(e));
    } finally {
      setActionPending(false);
    }
  }, [role?.id, role?.status, load]);

  // ─── RC 1.62 Phase F — clone-to-new-draft ──────────────────
  // Closed / archived roles are immutable governance records and
  // can never be reopened. The sanctioned reuse path is to clone
  // the role's structured definition into a fresh draft. The
  // source role is unaffected; a new role_id is created in
  // status='draft' with `(clone)` suffix on the title.
  const [clonePending, setClonePending] = useState(false);
  const handleClone = useCallback(async () => {
    if (!role?.id || clonePending) return;
    setClonePending(true);
    try {
      const resp = await cloneRole(role.id);
      const newId = resp?.role?.id;
      if (!newId) {
        toast.error('Clone did not return a new role id.');
        return;
      }
      toast.success('Cloned to draft. Edit and publish when ready.');
      navigate(`/employer/roles/${newId}`);
    } catch (e) {
      const detail = e?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'POTENTIAL_DUPLICATE_ROLE') {
        toast.error(
          'A role with this title already exists. Open the existing role or rename the source before cloning.',
        );
      } else {
        toast.error(formatBackendError(e) || 'Failed to clone role.');
      }
    } finally {
      setClonePending(false);
    }
  }, [role?.id, clonePending, navigate]);
  // ───────────────────────────────────────────────────────────
  // ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 p-8 text-sm text-slate-300"
        data-testid="role-detail-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading role…
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="p-6" data-testid="role-detail-error">
        <Card>
          <CardContent className="flex items-start gap-3 p-6">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-rose-400" />
            <div className="space-y-2">
              <div className="font-medium text-ndor-ink">
                {error || 'Role not found'}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={load}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Retry
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate('/employer/roles')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to roles
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const defn       = role.structured_definition_json || {};
  const compBand   = isObj(defn.comp_band) ? defn.comp_band : null;
  const responsibs = isList(defn.responsibilities)    ? defn.responsibilities    : [];
  const mustSkills = isList(defn.must_have_skills)    ? defn.must_have_skills    : [];
  const niceSkills = isList(defn.nice_to_have_skills) ? defn.nice_to_have_skills : [];

  const critRow = (criteria && criteria.criteria_exists !== false)
                    ? (criteria.criteria || criteria)
                    : null;
  const weights = (critRow && isObj(critRow.dimension_weights))
                    ? critRow.dimension_weights
                    : null;

  const isDraft         = role.status === 'draft';
  const isClosing       = role.status === 'closing';
  const isActiveLike    = role.status === 'active' || role.status === 'live';
  const isClosed        = role.status === 'closed';
  const isArchived      = role.status === 'archived';
  const isLegacyEditing = !isDraft;   // Slice A: only drafts get the legacy edit link.

  return (
    <div
      className="space-y-6 p-6 lg:p-8 max-w-screen-2xl mx-auto w-full"
      data-testid={`role-detail-${role.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/employer/roles')}
          data-testid="role-detail-back"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to roles
        </Button>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            data-testid="role-detail-refresh"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {isDraft ? (
            <Button
              size="sm"
              asChild
              data-testid="role-detail-edit-draft"
              title="Edit this draft"
            >
              <Link to={`/employer/roles/${role.id}/edit`}>
                <Edit3 className="mr-2 h-4 w-4" />
                Edit draft
              </Link>
            </Button>
          ) : null}
          {isDraft ? (
            <Button
              size="sm"
              variant="outline"
              onClick={openArchiveModal}
              data-testid="role-detail-archive-cta"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Discard draft
            </Button>
          ) : null}
          {isActiveLike ? (
            <Button
              size="sm"
              variant="outline"
              onClick={openCloseModal}
              data-testid="role-detail-close-cta"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Close role
            </Button>
          ) : null}
          {isClosing ? (
            <Button
              size="sm"
              onClick={openCloseModal}
              data-testid="role-detail-close-cta"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Close now
            </Button>
          ) : null}
          {isClosed ? (
            <Button
              size="sm"
              variant="outline"
              onClick={openArchiveModal}
              data-testid="role-detail-archive-cta"
            >
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
          ) : null}
          {(isClosed || isArchived) ? (
            <Button
              size="sm"
              onClick={handleClone}
              disabled={clonePending}
              data-testid="role-detail-clone-cta"
              title="Create a new draft role from this role's structured definition. The source role is not modified."
            >
              {clonePending
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Copy className="mr-2 h-4 w-4" />}
              Clone to Draft
            </Button>
          ) : null}
        </div>
      </div>

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-ndor-ink">
            {role.title || '(untitled)'}
          </h1>
          <RoleStatusBadge status={role.status} />
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-300">
          <span className="inline-flex items-center gap-1">
            <Briefcase className="h-3 w-3" />
            Created: {fmtTs(role.created_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Published: {fmtTs(role.published_at)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Updated: {fmtTs(role.updated_at)}
          </span>
        </div>
        {isLegacyEditing ? (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
            data-testid="role-detail-readonly-banner"
          >
            <ShieldAlert className="mt-0.5 h-4 w-4" />
            <div>
              This role is no longer editable from the wizard. Use the
              lifecycle controls above to close or archive it.
            </div>
          </div>
        ) : null}
        {isClosing ? (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
            data-testid="role-detail-closing-banner"
          >
            <Clock className="mt-0.5 h-4 w-4" />
            <div>
              This role is closing. Active matches are paused; downstream
              automation will finalise it to <code className="font-mono">closed</code>.
              You can <strong>Close now</strong> to finalise immediately.
            </div>
          </div>
        ) : null}
        {isArchived ? (
          <div
            className="flex items-start gap-2 rounded-md border border-slate-500/40 bg-slate-500/10 p-3 text-xs text-slate-200"
            data-testid="role-detail-archived-banner"
          >
            <Archive className="mt-0.5 h-4 w-4" />
            <div>
              This role is archived. It is no longer visible in the active
              roles list and cannot be re-opened.
            </div>
          </div>
        ) : null}
      </header>

      {/* Slice α + β + W1 — operational status band. Usage strip
          and bulk-eval job card read as one coherent zone so the
          operator's eye doesn't bounce between them. */}
      <section
        className="space-y-3"
        aria-label="Operational status"
        data-testid="role-detail-operational-band"
      >
        <RoleUsageStrip roleId={role.id} />
        <RoleBulkEvalJobCard
          roleId={role.id}
          onTerminal={() => setCandidatesRefreshToken((t) => t + 1)}
        />
      </section>

      <div data-testid="role-detail-tabs-wrapper">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* RC 1.62 Phase A (2026-02-28) — parent IA shell:
                Definition → Upload Dossiers → Candidates → Status.
              Default tab is "definition" (operator-locked Phase A).
              Upload Dossiers and Status are honest placeholder shells
              this phase; full wiring lands in Phase B (upload move +
              auto-tab-switch) and Phase E (read-only summary). The
              Candidates tab continues to host the existing dossier
              evaluation workflow byte-stable, plus a transitional mount
              of SurfacedCandidatesPlaceholder until Phase C sub-tab
              split. Active-tab styling preserved (4 tabs now). */}
          <TabsList
            className="bg-ndor-bg/40 border border-ndor-edge p-1 gap-1 h-auto"
            data-testid="role-detail-tabs"
          >
            <TabsTrigger
              value="definition"
              data-testid="role-detail-tab-definition"
              className="text-xs sm:text-sm font-medium text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] data-[state=active]:border-ndor-ink/30 hover:text-ndor-ink transition-colors"
            >
              Definition
            </TabsTrigger>
            <TabsTrigger
              value="upload"
              data-testid="role-detail-tab-upload"
              className="text-xs sm:text-sm font-medium text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] data-[state=active]:border-ndor-ink/30 hover:text-ndor-ink transition-colors"
            >
              Upload Dossiers
            </TabsTrigger>
            <TabsTrigger
              value="candidates"
              data-testid="role-detail-tab-candidates"
              className="text-xs sm:text-sm font-medium text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] data-[state=active]:border-ndor-ink/30 hover:text-ndor-ink transition-colors"
            >
              Candidates
            </TabsTrigger>
            <TabsTrigger
              value="status"
              data-testid="role-detail-tab-status"
              className="text-xs sm:text-sm font-medium text-ndor-ink-secondary data-[state=active]:bg-ndor-surface data-[state=active]:text-ndor-ink data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_0_#C9A45C] data-[state=active]:border-ndor-ink/30 hover:text-ndor-ink transition-colors"
            >
              Status
            </TabsTrigger>
          </TabsList>

          {/* ── Tab — Definition (role spec + criteria) ────────── */}
          <TabsContent
            value="definition"
            className="mt-4 focus-visible:outline-none"
            data-testid="role-detail-tab-content-definition"
          >
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
              <div className="space-y-6 lg:col-span-2 lg:space-y-8 max-h-[720px] overflow-y-auto pr-1">
                {/* Structured definition (context — moved out of the prime work column) */}
                <Card data-testid="role-detail-definition">
                  <CardHeader>
                    <CardTitle className="text-base">Role definition</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-slate-300">
                          Must-have domain
                        </dt>
                        <dd className="text-slate-100">
                          {defn.must_have_domain || <span className="text-slate-300">—</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-slate-300">
                          Working model
                        </dt>
                        <dd className="text-slate-100">
                          {defn.working_model || <span className="text-slate-300">—</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-slate-300">
                          Location
                        </dt>
                        <dd className="text-slate-100">
                          {defn.location || <span className="text-slate-300">—</span>}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-slate-300">
                          Compensation band
                        </dt>
                        <dd className="text-slate-100">
                          {compBand
                            ? `${compBand.currency || ''} ${compBand.min ?? '?'}–${compBand.max ?? '?'}`.trim()
                            : <span className="text-slate-300">—</span>}
                        </dd>
                      </div>
                    </dl>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                        Responsibilities
                      </div>
                      {responsibs.length > 0 ? (
                        <ul className="list-disc pl-5 text-slate-100">
                          {responsibs.map((r, i) => (<li key={i}>{r}</li>))}
                        </ul>
                      ) : (
                        <div className="text-slate-300">— none yet</div>
                      )}
                    </div>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                        Must-have skills
                      </div>
                      {mustSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {mustSkills.map((s, i) => (
                            <Badge
                              key={i}
                              className="bg-slate-500/10 text-slate-200 border border-slate-500/40"
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="text-slate-300">— none yet</div>
                      )}
                    </div>

                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                        Nice-to-have skills
                      </div>
                      {niceSkills.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {niceSkills.map((s, i) => (
                            <Badge
                              key={i}
                              className="bg-slate-500/10 text-slate-200 border border-slate-500/40"
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="text-slate-300">— none yet</div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Criteria mirror (raw read-only mirror; supplements the richer summary above) */}
                <Card data-testid="role-detail-criteria">
                  <CardHeader>
                    <CardTitle className="text-base">Evaluation criteria (raw)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 text-sm">
                    {!critRow ? (
                      <div className="text-slate-300" data-testid="role-detail-criteria-empty">
                        Criteria will appear here after this role is published.
                        Until then, evaluation defaults will be derived from the
                        role definition above.
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                            Dimension weights
                          </div>
                          {weights ? (
                            <div className="grid grid-cols-2 gap-2">
                              {['seniority', 'domain_alignment', 'evidence_strength', 'location', 'risk']
                                .map((k) => (
                                  <div
                                    key={k}
                                    className="flex items-center justify-between rounded-md border border-ndor-edge bg-ndor-bg/50 px-3 py-2"
                                    data-testid={`role-detail-weight-${k}`}
                                  >
                                    <span className="text-slate-200">{k}</span>
                                    <span className="font-mono text-slate-100">
                                      {weights[k] ?? 0}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <div className="text-slate-300">— defaults will be applied</div>
                          )}
                        </div>

                        <div>
                          <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                            Required criteria
                          </div>
                          {isList(critRow.required_criteria) ? (
                            <div className="flex flex-wrap gap-1.5">
                              {critRow.required_criteria.map((c, i) => (
                                <Badge
                                  key={i}
                                  className="bg-emerald-500/10 text-emerald-200 border border-emerald-500/40"
                                >
                                  {c}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <div className="text-slate-300">— none</div>
                          )}
                        </div>

                        <div>
                          <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                            Preferred criteria
                          </div>
                          {isList(critRow.preferred_criteria) ? (
                            <div className="flex flex-wrap gap-1.5">
                              {critRow.preferred_criteria.map((c, i) => (
                                <Badge
                                  key={i}
                                  className="bg-sky-500/10 text-sky-200 border border-sky-500/40"
                                >
                                  {c}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <div className="text-slate-300">— none</div>
                          )}
                        </div>

                        <div>
                          <div className="mb-1 text-xs uppercase tracking-wide text-slate-300">
                            Dealbreakers
                          </div>
                          {isObj(critRow.dealbreakers) && Object.keys(critRow.dealbreakers).length > 0 ? (
                            <div className="grid grid-cols-2 gap-1.5 text-xs">
                              {Object.entries(critRow.dealbreakers).map(([k, v]) => (
                                <div key={k} className="rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-rose-200">
                                  <span className="font-medium">{k}:</span> {String(v)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-slate-300">— none</div>
                          )}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Context column — evaluation rubric summary */}
              <aside className="space-y-6">
                <RoleCriteriaSummary role={role} />
              </aside>
            </div>
          </TabsContent>

          {/* ── Tab — Upload Dossiers (RC 1.62 Phase B · live;
              RC 1.62 Role Lifecycle Gate Hardening — calmly
              blocked on non-active states with publish-snapshot
              copy. The backend rejects with 409 ROLE_NOT_OPEN
              regardless; this UI keeps the operator out of a
              dead-end flow. ── */}
          <TabsContent
            value="upload"
            className="mt-4 focus-visible:outline-none"
            data-testid="role-detail-tab-content-upload"
          >
            {isActiveLike ? (
              <Card
                className="border-ndor-edge bg-ndor-surface"
                data-testid="role-detail-upload-card"
              >
                <CardHeader className="px-6 pt-6 pb-3">
                  <CardTitle className="text-base font-semibold text-ndor-ink">
                    Upload Dossiers
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 pb-6 space-y-3">
                  <p className="text-xs text-ndor-ink-muted leading-relaxed">
                    Add candidate dossiers one at a time, or upload a ZIP of
                    PDFs/DOCXs as a bulk batch. On a successful upload, the
                    view automatically switches to <strong>Candidates</strong>
                    so the newly-parsed rows surface immediately. Duplicates
                    stay on this tab so you can decide what to do next.
                  </p>
                  <RoleUploadPanel
                    roleId={role.id}
                    roleTitle={role.title}
                    onUploadComplete={onUploadSuccess}
                    onReviewFlaggedIntegrity={onReviewFlaggedIntegrity}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card
                className="border-ndor-edge bg-ndor-surface"
                data-testid="role-detail-upload-blocked"
              >
                <CardContent className="px-6 py-6 space-y-2">
                  <p className="text-sm font-medium text-ndor-ink">
                    {isDraft
                      ? 'Upload becomes available after publish.'
                      : 'Upload is closed for this role.'}
                  </p>
                  <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                    {isDraft
                      ? 'Publish this role to begin candidate intake and generate NDOR-Surfaced Candidates. NDOR matching runs once at publish and produces an immutable historical snapshot — no continuous rescoring.'
                      : isClosing
                        ? 'New dossier intake is paused while this role is closing. Reviewer triage and governance pack assembly remain available below.'
                        : isClosed
                          ? 'New dossier intake is closed. Historical evidence and governance packs remain available.'
                          : 'This role is archived and is read-only.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Tab — Candidates (RC 1.62 Phase C — split into two
              internal sub-tabs by RoleCandidatesWorkspace:
              {Company} Imported Candidates and NDOR-Surfaced
              Candidates.

              RC 1.62 Role Lifecycle Gate Hardening (2026-05-30):
              draft roles have no candidates yet (no upload, no
              NDOR snapshot) so we surface a calm publish prompt
              instead of an empty workspace. Closing / closed /
              archived continue to render the workspace because
              previously-imported candidates and the NDOR snapshot
              remain valid historical evidence. ── */}
          <TabsContent
            value="candidates"
            className="mt-4 focus-visible:outline-none"
            data-testid="role-detail-tab-content-candidates"
          >
            {isDraft ? (
              <Card
                className="border-ndor-edge bg-ndor-surface"
                data-testid="role-detail-candidates-draft-blocked"
              >
                <CardContent className="px-6 py-6 space-y-2">
                  <p className="text-sm font-medium text-ndor-ink">
                    No candidates yet — this role is still a draft.
                  </p>
                  <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                    Publish this role to begin candidate intake and
                    NDOR-Surfaced Candidates. NDOR matching runs
                    once at publish — the surfaced set is a
                    historical evidence snapshot, not a continuous
                    feed.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <RoleCandidatesWorkspace
                role={role}
                refreshToken={candidatesRefreshToken}
                integrityFilterRequest={pendingIntegrityFilter}
                onIntegrityFilterApplied={clearIntegrityFilterRequest}
              />
            )}
          </TabsContent>

          {/* ── Tab — Status (RC 1.62 Phase E · live operational
              governance surface). Composition of existing audited
              data sources (role object, /candidates, /governance-packs,
              ReviewerCoveragePanel, LifecycleTimelinePanel). NO
              hardcoded metrics; NO mutating affordance. ── */}
          <TabsContent
            value="status"
            className="mt-4 focus-visible:outline-none"
            data-testid="role-detail-tab-content-status"
          >
            <RoleStatusTab role={role} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Slice D — close + archive interstitials */}
      <CloseRoleModal
        open={closeModalOpen}
        status={role.status}
        onCancel={cancelAction}
        onConfirm={confirmClose}
        pending={actionPending}
        error={actionError}
      />
      <ArchiveRoleModal
        open={archiveModalOpen}
        status={role.status}
        onCancel={cancelAction}
        onConfirm={confirmArchive}
        pending={actionPending}
        error={actionError}
      />
    </div>
  );
}
